import { useInfiniteQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useApi } from './session';
import type { Page } from './types';

/** Lista paginada por cursor (`{ items, nextCursor }`) com "carregar mais". */
export function useCursorList<T>(key: QueryKey, path: string, query: Record<string, string | number | undefined> = {}, enabled = true) {
  const api = useApi();
  const q = useInfiniteQuery({
    queryKey: [...key, query],
    queryFn: ({ pageParam }) => api.get<Page<T>>(path, { query: { ...query, cursor: pageParam ?? undefined } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
  return { ...q, items: q.data?.pages.flatMap((p) => p.items) ?? [] };
}

/** Valor que só muda depois de `ms` sem digitação (busca). */
export function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Mutação que invalida as listas afetadas ao terminar. */
export function useApiMutation<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>, invalidate: QueryKey[] = []) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((k) => queryClient.invalidateQueries({ queryKey: k })));
    },
  });
}
